/**
 * Where the page's top chrome ENDS, for the layouts that allocate the space under it. Plain
 * TypeScript — this module imports NO React.
 *
 * ## Why a measured rect is the wrong answer on its own (kitecorp/transferify-ui#2473)
 *
 * The top bar plays a 0.6s entrance (`nav/normal/NavBar.styles.ts`, `fadeDown`: `translateY(-14px)`
 * to none), and `getBoundingClientRect` reports the TRANSFORMED box, so for the first 600ms the bar's
 * bottom reads anywhere from 14px short of its settled value to the value itself. Every caller here is
 * deciding a LAYOUT question — how much room is left between the chrome and the card — and a decorative
 * entrance is not part of that answer. Worse, it is not part of it non-deterministically: the two
 * callers below measure once at mount and re-measure only when a box they observe changes size, and a
 * transform changes no box's size, so whichever frame the first measurement happened to land on is the
 * answer the page keeps.
 *
 * That is exactly how ui#2473 presented. At 375x812 the recipient card leaves a 42.1px gap under the
 * settled bar, which is under the 44px a campaign strip needs, so the strip belongs below the card. On
 * one machine the first measurement sampled the bar at 53.2px instead of 56px, the gap read 44.9px, and
 * the strip was allocated ABOVE the card — while the same commit on the CI runner sampled the settled
 * bar and put it below. Neither browser was wrong about anything; they sampled different frames.
 *
 * Subtracting the transform's own vertical term gives the settled band from the FIRST measurement, on
 * every machine, with no listener for an animation that may already have finished by the time a hook
 * mounts. It keeps the page's scroll position in the answer, which a token would not: the bar is
 * `position: absolute`, so it really does leave with the page.
 */

/** A computed `transform` is always resolved to one of these two matrix forms, or to `none`. */
const COMPUTED_MATRIX = /^matrix(3d)?\(([^)]*)\)$/;
const MATRIX_2D_TRANSLATE_Y = 5;
const MATRIX_3D_TRANSLATE_Y = 13;

/**
 * The measured bottom of a box with its own vertical translation removed.
 *
 * Anything this cannot read — a rotation, a truncated matrix, an empty string — returns the measured
 * value unchanged rather than a guess: an un-corrected rect is the behaviour every caller had before,
 * so an unreadable transform costs nothing that was not already being paid.
 */
export function untransformedBottom(measuredBottom: number, transform: string): number {
	const matrix = COMPUTED_MATRIX.exec(transform.trim());
	if (matrix === null) return measuredBottom;
	const values = matrix[2].split(",").map(value => Number.parseFloat(value));
	const translateY = values[matrix[1] ? MATRIX_3D_TRANSLATE_Y : MATRIX_2D_TRANSLATE_Y];
	return Number.isFinite(translateY) ? measuredBottom - translateY : measuredBottom;
}

/** The settled bottom of the page's top bar, in viewport coordinates; 0 when the page has no bar. */
export function topbarBandBottom(header: HTMLElement | null): number {
	if (header === null) return 0;
	return untransformedBottom(header.getBoundingClientRect().bottom, getComputedStyle(header).transform);
}
